import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { useSendOrderClient } from "./sendOrderClient";
import { Status } from "../client/status";
import { useTableNameFromURL } from "./tableNameFromURL";
import {
  Box,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { PICK_UP_ORDER_TABLE_NAME } from "../ORDER_TABLE_NAME_TYPES";
import { isEmptyOrSpacesOnly } from "../dashbord/menuSpecificDashboard/stringUtils";
import { OrderType } from "../types/OrderType";
import AddressAutoComplete from "../AddressAutoComplete";
import { Link } from "react-router-dom";
import { useMenuIdFromURL } from "../hooks/menuIdFromURL";
import { useMenuData } from "../menu-provider/MenuDataProvider";
import { useCombinedCartAndMenuData } from "./combinedCartAndMenuData";

const DELIVERY_ORDER_TABLE_NAME = "Delivery";

export const OrderConfirm = ({
  onSuccess,
  onBack,
}: {
  onSuccess: (aOrder: OrderType) => void;
  onBack: () => void;
}) => {
  const { status, order, sendOrderRequest } = useSendOrderClient();

  const [customerName, setCustomerName] = useState<string>("");
  const [phoneNo, setPhoneNo] = useState<string>("");
  const [address, setAddress] = useState("");

  const { isDeliveryEnabled, isPickupEnabled, deliveryNote } = useMenuData();
  const { cartItemList, currency, taxPercentage, isItemWiseTaxEnabled } =
    useCombinedCartAndMenuData();

  const tableName = useTableNameFromURL();
  const menuId = useMenuIdFromURL();

  const alert = useAlert();

  const confirmOrderAction = () => {
    sendOrderRequest(customerName, phoneNo, address);
  };

  useEffect(() => {
    if (status == Status.Success) {
      order === undefined || onSuccess(order);
      return;
    }
  }, [status, onSuccess, order]);

  useEffect(() => {
    status == Status.Error && alert.error("Request failed.");
  }, [alert, status]);

  const isPlaceOrderDisabled =
    tableName == DELIVERY_ORDER_TABLE_NAME
      ? isDeliveryEnabled
        ? isEmptyOrSpacesOnly(customerName) ||
          isEmptyOrSpacesOnly(phoneNo) ||
          isEmptyOrSpacesOnly(address)
        : true
      : tableName == PICK_UP_ORDER_TABLE_NAME
      ? isEmptyOrSpacesOnly(customerName) || isEmptyOrSpacesOnly(phoneNo)
      : false;

  const showDeliveryPickupTabs =
    (tableName == DELIVERY_ORDER_TABLE_NAME ||
      tableName == PICK_UP_ORDER_TABLE_NAME) &&
    isDeliveryEnabled &&
    isPickupEnabled;

  const isDeliveryOrPickUp =
    tableName == PICK_UP_ORDER_TABLE_NAME ||
    tableName == DELIVERY_ORDER_TABLE_NAME;

  const subTotal = cartItemList.reduce(
    (acc, ci) => acc + ci.item.price * ci.quantity,
    0
  );

  const tax = isItemWiseTaxEnabled
    ? cartItemList.reduce((acc, ci) => {
        const itemTax = ci.item.tax;
        return itemTax !== undefined
          ? acc + (ci.item.price * ci.quantity * itemTax) / 100
          : acc;
      }, 0)
    : (subTotal * taxPercentage) / 100;

  const grandTotal = subTotal + tax;

  return (
    <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
      <Box flex={1} sx={{ overflowY: "auto" }}>
        <Box display="flex" alignItems="center" mb={1}>
          <IconButton onClick={onBack} size="small" aria-label="back to cart">
            <ArrowBack />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            Back to cart
          </Typography>
        </Box>

        <Box mb={1.5}>
          {cartItemList.map((ci, index) => (
            <Box
              key={index}
              display="flex"
              justifyContent="space-between"
              py={0.5}
            >
              <Typography variant="body2">
                {ci.item.name}
                {ci.quantity > 1 && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {" "}
                    × {ci.quantity}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2">
                {currency}
                {(ci.item.price * ci.quantity).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider />

        <Box mt={1} mb={2}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {isItemWiseTaxEnabled ? "Tax" : `Tax (${taxPercentage}%)`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currency}
              {tax.toFixed(2)}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight={600}>Total</Typography>
            <Typography fontWeight={600}>
              {currency}
              {grandTotal.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingTop: "16px",
          }}
        >
          {showDeliveryPickupTabs && (
            <Tabs value={tableName} sx={{ marginBottom: "8px" }}>
              {isDeliveryEnabled && isPickupEnabled && (
                <Tab
                  component={Link}
                  label={DELIVERY_ORDER_TABLE_NAME}
                  value={DELIVERY_ORDER_TABLE_NAME}
                  to={`/menu/${menuId}?t=Delivery`}
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                    border: "1px solid #9b9b9b8c",
                  }}
                />
              )}
              {isDeliveryEnabled && isPickupEnabled && (
                <Tab
                  component={Link}
                  label={PICK_UP_ORDER_TABLE_NAME}
                  value={PICK_UP_ORDER_TABLE_NAME}
                  to={`/menu/${menuId}?t=Pick-up`}
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                    border: "1px solid #9b9b9b8c",
                  }}
                />
              )}
            </Tabs>
          )}

          {isDeliveryOrPickUp && (
            <Typography
              sx={{ marginTop: 1, marginBottom: 1, fontWeight: "bold" }}
            >
              {deliveryNote}
            </Typography>
          )}

          {tableName == PICK_UP_ORDER_TABLE_NAME && (
            <Typography variant="h5">Pick-up order</Typography>
          )}
          {tableName == DELIVERY_ORDER_TABLE_NAME && (
            <Typography variant="h5">Delivery order</Typography>
          )}

          <TextField
            variant="standard"
            placeholder="Your name"
            label={isDeliveryOrPickUp ? "Your name" : "Your name (optional)"}
            onChange={(event) => {
              setCustomerName(event.target.value);
            }}
            value={customerName}
            sx={{ marginBottom: 1 }}
          />
          <TextField
            variant="standard"
            placeholder="Your phone"
            label={
              tableName == PICK_UP_ORDER_TABLE_NAME ||
              tableName == DELIVERY_ORDER_TABLE_NAME
                ? "Your phone"
                : "Your phone (optional)"
            }
            onChange={(event) => {
              setPhoneNo(event.target.value);
            }}
            value={phoneNo}
            sx={{ marginBottom: 1 }}
          />

          {tableName == DELIVERY_ORDER_TABLE_NAME && (
            <AddressAutoComplete
              address={address}
              onAddressChange={setAddress}
              variant="standard"
            />
          )}
        </Box>
      </Box>

      <Box display="flex" justifyContent="center" pt={1.5}>
        <LoadingButton
          onClick={confirmOrderAction}
          variant="contained"
          disabled={isPlaceOrderDisabled}
          loading={status === Status.Pending}
          data-testid="test-id-confirm-order-button"
          sx={{ width: "140px" }}
        >
          Place Order
        </LoadingButton>
      </Box>
    </Box>
  );
};
